import { useEffect, useState, useCallback } from "react";
import { Plus, Building2, Users, Settings2, FileText, Download } from "lucide-react";
import { api, apiErr, openPdf } from "@/lib/api";
import { toast } from "sonner";
import { Loading, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const STATUS_OPTS = ["未契約", "トライアル", "契約中", "停止", "解約"];
const STATUS_CLS = {
  契約中: "bg-emerald-500/15 text-emerald-400",
  トライアル: "bg-blue-500/15 text-blue-400",
  未契約: "bg-slate-700 text-slate-300",
  停止: "bg-amber-500/15 text-amber-400",
  解約: "bg-rose-500/15 text-rose-400",
};

const EMPTY = { name: "", corporate_number: "", representative: "", email: "", phone: "", address: "", admin_login_id: "", admin_name: "", admin_password: "" };

export default function PlatformCompanies() {
  const [items, setItems] = useState(null);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null); // company for status change
  const [statusForm, setStatusForm] = useState({ contract_status: "未契約", plan: "", trial_days: 30 });
  const [invCompany, setInvCompany] = useState(null);
  const [invMonth, setInvMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [invHistory, setInvHistory] = useState([]);

  const load = useCallback(() => {
    api.get("/platform/companies").then(({ data }) => setItems(data));
    api.get("/platform/plans").then(({ data }) => setPlans(data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name || !form.admin_login_id || !form.admin_name || !form.admin_password) {
      toast.error("法人名・管理者情報は必須です"); return;
    }
    try {
      await api.post("/platform/companies", form);
      toast.success("法人を作成しました（未契約状態）");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openStatus = (c) => {
    setEditing(c);
    setStatusForm({ contract_status: c.contract_status || "未契約", plan: c.plan || "", trial_days: 30 });
  };

  const saveStatus = async () => {
    try {
      await api.put(`/platform/companies/${editing.id}/status`, statusForm);
      toast.success("契約状態を更新しました");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const openInvoice = (c) => {
    setInvMonth(new Date().toISOString().slice(0, 7));
    setInvCompany(c);
    setInvHistory([]);
    api.get(`/platform/companies/${c.id}/invoices`).then(({ data }) => setInvHistory(data)).catch(() => {});
  };

  const issueInvoice = async () => {
    try {
      await openPdf(`/platform/companies/${invCompany.id}/invoice?period=${invMonth}`);
      const { data } = await api.get(`/platform/companies/${invCompany.id}/invoices`);
      setInvHistory(data);
      toast.success("請求書を発行しました");
    } catch (e) { toast.error(apiErr(e, "請求書の発行に失敗しました")); }
  };

  const redownload = async (inv) => {
    try { await openPdf(`/platform/companies/${invCompany.id}/invoice?period=${inv.period}`); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!items) return <Loading />;
  const fields = [["name", "法人名 *"], ["corporate_number", "法人番号"], ["representative", "代表者名"], ["email", "メール"], ["phone", "電話番号"], ["address", "住所"]];

  return (
    <div data-testid="platform-companies">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">法人管理</h1>
          <p className="text-sm text-slate-400 mt-1">顧客法人の作成・契約状態の管理</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-company-btn" className="bg-amber-500 hover:bg-amber-600 text-slate-900"><Plus className="w-4 h-4 mr-1" /> 法人を新規作成</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>法人を新規作成（未契約）</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                {fields.map(([k, l]) => (
                  <div key={k} className={k === "address" ? "sm:col-span-2" : ""}>
                    <Label>{l}</Label>
                    <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1" data-testid={`company-${k}`} />
                  </div>
                ))}
              </div>
              <div className="rounded-lg bg-slate-100 p-3 space-y-3">
                <div className="text-xs font-medium text-slate-500">初期の法人管理者アカウント</div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><Label>管理者ログインID *</Label><Input value={form.admin_login_id} onChange={(e) => setForm({ ...form, admin_login_id: e.target.value })} className="mt-1" data-testid="company-admin-login" /></div>
                  <div><Label>管理者氏名 *</Label><Input value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="mt-1" data-testid="company-admin-name" /></div>
                  <div className="sm:col-span-2"><Label>初期パスワード（8文字以上）*</Label><Input type="text" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="mt-1" data-testid="company-admin-pw" /></div>
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={create} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-company-btn">作成</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
        {items.length ? (
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-400">法人名</TableHead>
                <TableHead className="text-slate-400 text-center">事業所</TableHead>
                <TableHead className="text-slate-400 text-center">ユーザー</TableHead>
                <TableHead className="text-slate-400">契約状態</TableHead>
                <TableHead className="text-slate-400">プラン</TableHead>
                <TableHead className="text-slate-400">作成日</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} className="border-slate-800 hover:bg-slate-800/50" data-testid={`company-row-${c.id}`}>
                  <TableCell className="font-medium text-slate-100"><div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-500" />{c.name}</div></TableCell>
                  <TableCell className="text-center text-slate-300">{c.office_count}</TableCell>
                  <TableCell className="text-center text-slate-300"><span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5 text-slate-500" />{c.user_count}</span></TableCell>
                  <TableCell><span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[c.contract_status] || "bg-slate-700 text-slate-300"}`}>{c.contract_status || "未契約"}</span></TableCell>
                  <TableCell className="text-slate-400 text-sm">{c.subscription?.plan_name || "-"}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{(c.created_at || "").slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" onClick={() => openStatus(c)} data-testid={`status-btn-${c.id}`}><Settings2 className="w-4 h-4 mr-1" />契約変更</Button>
                      {Number(c.subscription?.amount) > 0 && (
                        <Button size="sm" variant="outline" className="border-slate-700 text-amber-400 hover:bg-slate-800" onClick={() => openInvoice(c)} data-testid={`invoice-btn-${c.id}`}><FileText className="w-4 h-4 mr-1" />請求書</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <EmptyState title="法人がありません" hint="「法人を新規作成」から追加してください" />}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>契約状態の変更 — {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>契約状態</Label>
              <Select value={statusForm.contract_status} onValueChange={(v) => setStatusForm({ ...statusForm, contract_status: v })}>
                <SelectTrigger className="mt-1" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s} data-testid={`status-opt-${s}`}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {(statusForm.contract_status === "契約中" || statusForm.contract_status === "トライアル") && (
              <div>
                <Label>プラン</Label>
                <Select value={statusForm.plan || ""} onValueChange={(v) => setStatusForm({ ...statusForm, plan: v })}>
                  <SelectTrigger className="mt-1" data-testid="status-plan-select"><SelectValue placeholder="プランを選択" /></SelectTrigger>
                  <SelectContent>{plans.map((p) => <SelectItem key={p.lookup_key} value={p.lookup_key}>{p.name}（¥{Number(p.price).toLocaleString()}/{p.interval}）</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {statusForm.contract_status === "トライアル" && (
              <div><Label>トライアル日数</Label><Input type="number" value={statusForm.trial_days} onChange={(e) => setStatusForm({ ...statusForm, trial_days: Number(e.target.value) })} className="mt-1" data-testid="trial-days" /></div>
            )}
          </div>
          <DialogFooter><Button onClick={saveStatus} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-status-btn">保存</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!invCompany} onOpenChange={(o) => !o && setInvCompany(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>請求書PDFの発行 — {invCompany?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">対象月を選んで請求書PDFを発行します。プラン「{invCompany?.subscription?.plan_name || invCompany?.plan || "-"}」に基づき、消費税10%込みの金額で作成されます。</p>
            <div>
              <Label>対象月</Label>
              <Input type="month" value={invMonth} onChange={(e) => setInvMonth(e.target.value)} className="mt-1 max-w-[200px]" data-testid="invoice-month" />
            </div>
            {invHistory.length > 0 && (
              <div>
                <Label>発行履歴</Label>
                <div className="mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
                  {invHistory.map((inv) => (
                    <div key={inv.invoice_no} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`inv-hist-${inv.period}`}>
                      <div>
                        <div className="text-slate-800 font-medium">{inv.period_label}</div>
                        <div className="text-xs text-slate-400">{inv.invoice_no}・¥{Number(inv.total).toLocaleString()}（税込）</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => redownload(inv)} data-testid={`inv-dl-${inv.period}`}><Download className="w-4 h-4 mr-1" />再DL</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={issueInvoice} className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="issue-invoice-btn"><FileText className="w-4 h-4 mr-1" />請求書PDFを発行</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
